import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FaMapMarkerAlt, FaClock, FaUsers, FaShieldAlt, 
  FaLeaf, FaGlobeAfrica, FaFemale, FaEnvelope, 
  FaHome, FaWhatsapp, FaStar, FaCheck, FaPhone,
  FaFacebook, FaInstagram, FaTwitter, FaUser,
  FaComment
} from 'react-icons/fa';

const Home = () => {
  // Background images for rotating gallery
  const backgroundImages = [
    '/images/ethiopia-hero.jpg',
    '/images/lalibela.jpg',
    '/images/addis-tour.jpg',
    '/images/debre-libanos.jpg',
    '/images/omo-valley.jpg',
    '/images/danakil.jpg',
    '/images/bale.jpg'
  ];

  const [currentBgIndex, setCurrentBgIndex] = useState(0);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    interestedIn: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBgIndex((prevIndex) => 
        prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate form submission
    setTimeout(() => {
      setIsSubmitting(false);
      setSubmitStatus('success');
      setFormData({ firstName: '', lastName: '', email: '', interestedIn: '', message: '' });
      setTimeout(() => setSubmitStatus(null), 5000);
    }, 1500);
  };

  // WhatsApp message templates
  const whatsappNumber = "251912093264";
  const generalBookingMsg = "Hello! I'm interested in booking a tour with Encourage Women Ethiopia. Can you help me plan my trip?";
  const generalWhatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(generalBookingMsg)}`;

  // VALUE PROPS - 4 Cards
  const valueProps = [
    { icon: <FaMapMarkerAlt />, title: "Based in Addis Ababa", desc: "Local expertise from Ethiopia's vibrant capital" },
    { icon: <FaGlobeAfrica />, title: "Local Ethiopian Tour Operator", desc: "Authentic experiences by those who know Ethiopia best" },
    { icon: <FaFemale />, title: "Women Empowerment", desc: "10% of profits support women's associations" },
    { icon: <FaUsers />, title: "Community-Based Tourism", desc: "Responsible travel that gives back" }
  ];

  // FEATURED TOURS
  const featuredTours = [
    {
      category: "Day Tour",
      title: "Addis Ababa City Tour – Full Day",
      description: "Discover Ethiopia's vibrant capital city, visit the National Museum, Holy Trinity Cathedral, Mercato market, and experience local cuisine.",
      duration: "Full Day (8-10 hours)",
      location: "Addis Ababa",
      price: "On Request",
      image: "/images/addis-tour.jpg",
      rating: 4.9,
      reviews: 128,
      tourId: "addis-ababa-city-tour"
    },
    {
      category: "Day Tour",
      title: "Debre Libanos & Portuguese Bridge – Day Tour",
      description: "Visit the historic Debre Libanos Monastery, explore the Portuguese Bridge, and enjoy breathtaking views of the Blue Nile Gorge.",
      duration: "1 Day",
      location: "Debre Libanos, Oromia",
      price: "On Request",
      image: "/images/debre-libanos.jpg",
      rating: 4.8,
      reviews: 94,
      tourId: "debre-libanos"
    },
    {
      category: "Multi-Day",
      title: "Lalibela Historical Tour – Multi-Day",
      description: "Explore the legendary rock-hewn churches of Lalibela, a UNESCO World Heritage Site and spiritual heart of Ethiopian Christianity.",
      duration: "3-5 Days",
      location: "Lalibela, Northern Ethiopia",
      price: "On Request",
      image: "/images/lalibela.jpg",
      rating: 5.0,
      reviews: 156,
      tourId: "lalibela"
    }
  ];

  // STATS - CORRECT NUMBERS with GOLD color
  const stats = [
    { number: "500+", label: "Happy Travelers" },
    { number: "10+", label: "Years Experience" },
    { number: "50+", label: "Tours Completed" },
    { number: "15+", label: "Destinations" }
  ];

  // WHY US FEATURES - Icons will be gold
  const whyUsFeatures = [
    { icon: <FaFemale />, title: "Women-Focused Travel", desc: "Safe, empowering experiences designed especially for women explorers." },
    { icon: <FaShieldAlt />, title: "Safe & Secure", desc: "Professional local female guides and vetted accommodations throughout." },
    { icon: <FaUsers />, title: "Small Groups", desc: "Intimate group sizes for personalized attention and deeper connections." },
    { icon: <FaGlobeAfrica />, title: "Local Expertise", desc: "Authentic Ethiopian culture and nature from those who know it best." },
    { icon: <FaLeaf />, title: "Ethical Tourism", desc: "Community-based tourism that gives back to local communities." }
  ];

  // DESTINATIONS - WITH IMAGES (ALL 6 DISPLAYED)
  const destinations = [
    { 
      category: "The Capital City", 
      title: "Addis Ababa City Tours", 
      desc: "Discover Ethiopia's vibrant capital with its rich history, museums, and local culture.", 
      link: "/destinations/addis-ababa",
      image: "/images/addis-ababa.jpg",
      fallback: "https://images.unsplash.com/photo-1577805945776-1fc0126b6c6c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    { 
      category: "Historic Monastery", 
      title: "Debre Libanos & Blue Nile Gorge", 
      desc: "Visit the famous monastery, Portuguese Bridge, and breathtaking Blue Nile Gorge.", 
      link: "/destinations/debre-libanos",
      image: "/images/debre-libanos.jpg",
      fallback: "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    { 
      category: "UNESCO World Heritage", 
      title: "Lalibela Rock-Hewn Churches", 
      desc: "Explore 11 medieval rock-cut churches carved from living stone.", 
      link: "/destinations/lalibela",
      image: "/images/lalibela.jpg",
      fallback: "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    { 
      category: "Living Cultures", 
      title: "Omo Valley Cultural Tours", 
      desc: "Meet indigenous tribes with ancient traditions still alive today.", 
      link: "/destinations/omo-valley",
      image: "/images/omo-valley.jpg",
      fallback: "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    { 
      category: "Alien Landscapes", 
      title: "Danakil Depression", 
      desc: "Witness Earth's most extreme and colorful volcanic terrain.", 
      link: "/destinations/danakil",
      image: "/images/danakil.jpg",
      fallback: "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    },
    { 
      category: "Nature & Wildlife", 
      title: "Bale Mountains", 
      desc: "Trek through dramatic peaks and spot endemic wildlife like the Ethiopian Wolf.", 
      link: "/destinations/bale-mountains",
      image: "/images/bale.jpg",
      fallback: "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
    }
  ];

  // IMPACT ITEMS
  const impactItems = [
    { title: "Women's Associations", desc: "Supporting local women's groups and cooperatives" },
    { title: "Community Projects", desc: "Funding education and skill development programs" },
    { title: "Sustainable Tourism", desc: "Building a responsible travel ecosystem" }
  ];

  // CORE VALUES
  const coreValues = [
    { icon: "💪", title: "Women Empowerment", desc: "Creating safe, inspiring travel experiences designed especially for women adventurers." },
    { icon: "🛡️", title: "Safe Travel", desc: "Expert local guides, vetted accommodations, and comprehensive travel support at every step." },
    { icon: "🌍", title: "Cultural Immersion", desc: "Authentic connections with local communities and deep exploration of Ethiopian heritage." },
    { icon: "🤝", title: "Community", desc: "Building lasting friendships through shared adventures and transformative experiences." }
  ];

  // Function to handle "View All Destinations" click - stays on home page and scrolls to destinations
  const handleViewAllDestinations = (e) => {
    e.preventDefault();
    document.getElementById('destinations')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-white font-sans text-gray-700 antialiased">
      
      {/* ===== WHATSAPP FLOATING BUTTON - GREEN ===== */}
      <a
        href={generalWhatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#20BA5C] text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group whatsapp-pulse"
        style={{ boxShadow: '0 10px 25px -5px rgba(37, 211, 102, 0.4)' }}
        aria-label="Chat on WhatsApp"
      >
        <FaWhatsapp size={28} />
        <span className="max-w-0 overflow-hidden group-hover:max-w-xs group-hover:ml-2 transition-all duration-300 whitespace-nowrap font-medium">
          Chat with us
        </span>
      </a>

      {/* ===== HERO SECTION - ID="HOME" - TEXT SIZE REDUCED BY HALF, PADDING UPDATED ===== */}
      <section 
        id="home"
        className="relative bg-cover bg-center text-white min-h-screen flex items-center justify-center"
        style={{
          backgroundImage: `linear-gradient(0deg, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)), url('${backgroundImages[currentBgIndex]}')`,
        }}
      >
        <div className="container mx-auto px-4 text-center relative z-10 max-w-5xl">
          {/* Main heading - text size reduced by half */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-3 tracking-tight text-white">
            Encourage Women Ethiopia
          </h1>
          
          {/* Sub heading - text size reduced by half */}
          <p className="text-xl md:text-2xl lg:text-3xl mb-4 text-[#FFD700] font-semibold">
            Tour and Travel
          </p>
          
          {/* Description - text size reduced by half */}
          <p className="text-base md:text-lg lg:text-xl max-w-3xl mx-auto mb-8 text-white/90 leading-relaxed">
            Travel Bold. Travel Together. Discover Addis Ababa, Lalibela, Debre Libanos & Ethiopia's Hidden Wonders.
          </p>
          
          {/* Buttons with proper padding like live site */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            {/* GREEN WhatsApp button */}
            <a 
              href={generalWhatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-[#25D366] hover:bg-[#20BA5C] text-white px-6 py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-200 shadow-lg hover:shadow-xl inline-flex items-center gap-2"
            >
              <FaWhatsapp size={18} />
              Book Your Ethiopia Tour on WhatsApp
            </a>
            
            {/* GOLD Explore button */}
            <a 
              href="/#tours"
              className="bg-[#FFD700] hover:bg-[#FFC800] text-gray-900 px-6 py-3 rounded-full font-semibold text-sm md:text-base transition-all duration-200 shadow-lg hover:shadow-xl"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('tours')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Explore Our Tours
            </a>
          </div>
        </div>
      </section>

      {/* ===== VALUE PROPS - 4 CARDS ===== */}
      <section className="py-16 md:py-20 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {valueProps.map((prop, i) => (
              <div key={i} className="flex items-start gap-4 p-6 bg-white rounded-xl shadow-sm hover:shadow-md transition-shadow duration-300 border border-gray-100">
                {/* GOLD icons */}
                <span className="text-2xl text-[#FFD700]">{prop.icon}</span>
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">{prop.title}</h4>
                  <p className="text-sm text-gray-600">{prop.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FEATURED TOURS - ID="TOURS" ===== */}
      <section id="tours" className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-[#FFD700] text-sm font-semibold uppercase tracking-[0.25em] mb-4">Featured Tours</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Most Popular Experiences</h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              From day trips around Addis Ababa to multi-day adventures across Ethiopia's historic northern route—all bookable directly via WhatsApp.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {featuredTours.map((tour, i) => {
              const tourWhatsappUrl = `https://wa.me/${whatsappNumber}?text=${encodeURIComponent(`I'm interested in ${tour.title}. Can you provide more information about pricing and availability?`)}`;
              
              return (
                <div 
                  key={i} 
                  className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
                >
                  <div 
                    className="h-56 md:h-64 bg-cover bg-center relative group-hover:scale-105 transition-transform duration-500" 
                    style={{ backgroundImage: `url(${tour.image})` }}
                  >
                    <span className="absolute top-4 left-4 bg-white text-[#FFD700] px-4 py-2 rounded-full text-xs font-semibold uppercase tracking-wide shadow-md z-10">
                      {tour.category}
                    </span>
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex items-center text-yellow-400">
                        <FaStar />
                        <FaStar />
                        <FaStar />
                        <FaStar />
                        <FaStar />
                      </div>
                      <span className="text-sm text-gray-500">({tour.reviews} reviews)</span>
                    </div>
                    <h4 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 leading-tight group-hover:text-[#0b5e5e] transition-colors">
                      {tour.title}
                    </h4>
                    <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-4 line-clamp-2">{tour.description}</p>
                    <div className="flex items-center gap-4 text-sm text-gray-500 mb-5">
                      {/* GOLD icons */}
                      <span className="flex items-center gap-1.5">
                        <FaClock className="text-[#FFD700]" /> {tour.duration}
                      </span>
                      <span className="flex items-center gap-1.5">
                        <FaMapMarkerAlt className="text-[#FFD700]" /> {tour.location.split(',')[0]}
                      </span>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="font-bold text-gray-900">Price: {tour.price}</span>
                      {/* GREEN WhatsApp button */}
                      <a 
                        href={tourWhatsappUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-[#25D366] hover:bg-[#20BA5C] text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-colors duration-200 inline-flex items-center gap-2"
                      >
                        <FaWhatsapp size={16} />
                        Book on WhatsApp
                      </a>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          <div className="text-center mt-12">
            {/* GOLD View All button - STAYS ON HOME PAGE */}
            <a 
              href="/#tours"
              className="inline-block bg-[#FFD700] hover:bg-[#FFC800] text-gray-900 px-8 py-3 rounded-full font-semibold text-base transition-all duration-200 shadow-md"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('tours')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              View All Tours
            </a>
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-20 md:py-24 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-[#FFD700] text-sm font-semibold uppercase tracking-[0.25em] mb-4">Why Travel With Us</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Why Choose Encourage Women Ethiopia Tour and Travel?</h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              Experience Ethiopia with confidence, community, and care. We create transformative journeys designed to empower and inspire.
            </p>
          </div>

          {/* STATS - Numbers in GOLD */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto mb-20">
            {stats.map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-4xl md:text-5xl font-bold text-[#FFD700] mb-2">{stat.number}</div>
                <div className="text-sm text-gray-500 uppercase tracking-wider">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* FEATURES - Icons in GOLD */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8">
            {whyUsFeatures.map((feature, i) => (
              <div key={i} className="text-center">
                <div className="bg-[#FFD700] bg-opacity-10 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-[#FFD700] text-2xl">
                  {feature.icon}
                </div>
                <h4 className="font-bold text-gray-900 mb-2">{feature.title}</h4>
                <p className="text-sm text-gray-500 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BALE MOUNTAINS IMAGE GALLERY - NO TEXT, JUST IMAGES ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Image 1: Bale Mountains Sunset */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
              <img 
                src="/images/bale-sunset.jpg" 
                alt="Bale Mountains sunset"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                }}
              />
            </div>
            
            {/* Image 2: Walia Ibex */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
              <img 
                src="/images/walia-ibex.jpg" 
                alt="Walia Ibex"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                }}
              />
            </div>
            
            {/* Image 3: Camping Adventure */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
              <img 
                src="/images/camping-adventure.jpg" 
                alt="Camping adventure"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                }}
              />
            </div>
            
            {/* Image 4: Ethiopian Wolf */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
              <img 
                src="/images/ethiopian-wolf.jpg" 
                alt="Ethiopian Wolf"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                }}
              />
            </div>
            
            {/* Image 5: Gelada Baboon */}
            <div className="group relative overflow-hidden rounded-2xl shadow-lg aspect-square">
              <img 
                src="/images/gelada-baboon.jpg" 
                alt="Gelada Baboon"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://images.unsplash.com/photo-1597088758744-5bff39c7ca96?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80";
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* ===== ABOUT US - ID="ABOUT" ===== */}
      <section id="about" className="py-20 md:py-24 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="text-center mb-12">
            <h2 className="text-[#FFD700] text-sm font-semibold uppercase tracking-[0.25em] mb-4">About Us</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Encourage Women Ethiopia Tour and Travel</h3>
          </div>
          
          <div className="space-y-6 text-gray-700 leading-relaxed">
            <p className="text-lg">
              Encourage Women Ethiopia Tour and Travel is a women-led travel company based in Ethiopia, created with a clear mission: to empower women through safe, meaningful, and authentic travel experiences.
            </p>
            <p className="text-lg">
              Founded by <span className="font-semibold text-[#FFD700]">Atsedemariam Mamo</span>, a passionate and visionary Ethiopian woman, the company was born from her deep love for her country and her strong belief that travel can be a powerful tool for confidence, connection, and transformation. Atsedemariam has always been inspired by the strength, resilience, and creativity of women, and she envisioned a travel company that not only showcases Ethiopia's beauty but also uplifts women—both travelers and local communities.
            </p>
            <p className="text-lg">
              At Encourage Women Ethiopia Tour and Travel, we specialize in thoughtfully designed journeys across Ethiopia, including cultural and historical tours, nature and adventure experiences, women-only group trips, and customized private itineraries. Our tours highlight Ethiopia's rich heritage, breathtaking landscapes, and vibrant traditions while prioritizing safety, comfort, and authentic local interaction.
            </p>
            <p className="text-lg">
              We proudly work with local women guides, women-owned businesses, and community initiatives, ensuring that tourism creates positive impact and shared opportunity. Whether you are a solo traveler, part of a women's group, or seeking a personalized experience, we are here to help you explore Ethiopia with confidence and purpose.
            </p>
          </div>
          
          <div className="text-center mt-10 mb-16">
            <p className="text-2xl md:text-3xl font-semibold text-[#FFD700] italic">"Travel Bold. Travel Together."</p>
          </div>
          
          {/* VISION & MISSION - GOLD headings */}
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
              <h4 className="text-[#FFD700] font-bold text-xl mb-4">Our Vision</h4>
              <p className="text-gray-600 leading-relaxed">To create a world where women feel confident to explore, connect, and grow through travel.</p>
            </div>
            <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
              <h4 className="text-[#FFD700] font-bold text-xl mb-4">Our Mission</h4>
              <p className="text-gray-600 leading-relaxed">To deliver safe, ethical, and inspiring travel experiences that celebrate Ethiopia's culture while empowering women and local communities.</p>
            </div>
          </div>
          
          {/* CORE VALUES - Icons in GOLD */}
          <div>
            <h4 className="text-center text-2xl font-bold text-gray-900 mb-10">Our Core Values</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {coreValues.map((value, i) => (
                <div key={i} className="text-center">
                  <div className="text-[#FFD700] text-3xl mb-3">{value.icon}</div>
                  <h5 className="font-semibold text-gray-900 mb-2">{value.title}</h5>
                  <p className="text-xs text-gray-500 leading-relaxed">{value.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== DESTINATIONS - ID="DESTINATIONS" WITH IMAGES ===== */}
      <section id="destinations" className="py-20 md:py-24 bg-white">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-[#FFD700] text-sm font-semibold uppercase tracking-[0.25em] mb-4">Explore Ethiopia</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Destinations & Tours</h3>
            <p className="text-lg text-gray-600 leading-relaxed">
              From ancient rock churches to otherworldly volcanic landscapes, Ethiopia offers experiences unlike anywhere else on Earth.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {destinations.map((dest, i) => (
              <Link 
                to={dest.link}
                key={i} 
                className="bg-white rounded-2xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 group"
              >
                <div className="h-56 overflow-hidden">
                  <img 
                    src={dest.image}
                    alt={dest.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = dest.fallback;
                    }}
                  />
                </div>
                <div className="p-6">
                  <p className="text-sm font-semibold text-[#FFD700] uppercase tracking-wider mb-2">{dest.category}</p>
                  <h4 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-[#FFD700] transition-colors">{dest.title}</h4>
                  <p className="text-gray-600 leading-relaxed">{dest.desc}</p>
                </div>
              </Link>
            ))}
          </div>
          
          <div className="text-center">
            {/* GOLD View All button - STAYS ON HOME PAGE AND SCROLLS TO DESTINATIONS */}
            <a 
              href="/#destinations"
              onClick={handleViewAllDestinations}
              className="inline-block bg-[#FFD700] hover:bg-[#FFC800] text-gray-900 px-8 py-3 rounded-full font-semibold text-base transition-all duration-200 shadow-md cursor-pointer"
            >
              View All Destinations
            </a>
          </div>
        </div>
      </section>

      {/* ===== IMPACT SECTION ===== */}
      <section className="py-20 md:py-24 bg-[#0e7c7c] text-white">
        <div className="container mx-auto px-4 text-center max-w-5xl">
          <div className="text-5xl mb-6">💪</div>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Empowering Women Through Travel</h2>
          <p className="text-2xl md:text-3xl font-semibold mb-6 italic">"10% of our profits support women's associations and community projects across Ethiopia."</p>
          <p className="text-lg md:text-xl mb-12 text-white/90 leading-relaxed max-w-3xl mx-auto">
            When you travel with us, you're not just exploring Ethiopia—you're contributing to a movement that uplifts women and strengthens communities throughout the country.
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {impactItems.map((item, i) => (
              <div key={i}>
                <h4 className="text-xl font-bold mb-3 text-[#FFD700]">{item.title}</h4>
                <p className="text-white/80">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== READY TO EXPLORE ETHIOPIA ===== */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[#0b5e5e] to-[#0e7c7c] text-white">
        <div className="container mx-auto px-4 max-w-5xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">Ready to Explore Ethiopia?</h2>
            <p className="text-xl md:text-2xl max-w-3xl mx-auto text-white/90">
              Start planning your unforgettable Ethiopian adventure today. Our team is ready to help you create memories that last a lifetime.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 mb-10">
            {/* Book on WhatsApp - GREEN */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20">
              <FaWhatsapp size={40} className="mx-auto mb-4 text-[#25D366]" />
              <h3 className="text-xl font-bold mb-2 text-[#FFD700]">Book on WhatsApp</h3>
              <a 
                href={generalWhatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-2xl font-semibold hover:underline text-[#25D366]"
              >
                +251 912 093 264
              </a>
              <p className="text-sm text-white/80 mt-2">Click to chat instantly</p>
            </div>
            
            {/* Location & Hours - GOLD icons */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <FaMapMarkerAlt className="text-xl flex-shrink-0 text-[#FFD700]" />
                  <span>Bole Sub City, Addis Ababa</span>
                </div>
                <div className="flex items-center gap-3">
                  <FaClock className="text-xl flex-shrink-0 text-[#FFD700]" />
                  <span>Mon–Sun: 9AM–6PM (EAT)</span>
                </div>
                <div className="flex items-center gap-3">
                  <FaGlobeAfrica className="text-xl flex-shrink-0 text-[#FFD700]" />
                  <span>24/7 Online Support</span>
                </div>
              </div>
            </div>
            
            {/* Get In Touch Button - GOLD */}
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 flex flex-col items-center justify-center">
              <FaEnvelope className="text-4xl mb-3 text-[#FFD700]" />
              <h3 className="text-xl font-bold mb-2 text-[#FFD700]">Get In Touch</h3>
              <a 
                href="/#contact"
                className="inline-block bg-[#FFD700] hover:bg-[#FFC800] text-gray-900 px-6 py-2 rounded-full font-semibold transition-colors"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
                }}
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ===== START YOUR JOURNEY TODAY + CONTACT FORM - ID="CONTACT" ===== */}
      <section id="contact" className="py-20 md:py-24 bg-[#F9FAFB]">
        <div className="container mx-auto px-4 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Start Your Journey Today</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Ready to embark on your Ethiopian adventure? We'd love to hear from you. Our travel experts are here to help you plan the perfect trip.
            </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-10">
            {/* LEFT COLUMN - Contact Info - GOLD icons */}
            <div className="space-y-8">
              {/* Visit Us */}
              <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaHome className="text-[#FFD700]" /> Visit Us
                </h3>
                <p className="text-gray-600 mb-2">Bole Road, Near Edna Mall</p>
                <p className="text-gray-600">Addis Ababa, Ethiopia</p>
              </div>
              
              {/* Call Us */}
              <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaPhone className="text-[#FFD700]" /> Call Us
                </h3>
                <a 
                  href="tel:+251912093264" 
                  className="text-gray-600 hover:text-[#FFD700] transition-colors text-lg"
                >
                  +251 912 093 264
                </a>
              </div>
              
              {/* Email Us */}
              <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaEnvelope className="text-[#FFD700]" /> Email Us
                </h3>
                <p className="text-gray-600 mb-2">
                  <a href="mailto:info@encouragewomen.com" className="hover:text-[#FFD700] transition-colors">
                    info@encouragewomen.com
                  </a>
                </p>
                <p className="text-gray-600">
                  <a href="mailto:bookings@encouragewomen.com" className="hover:text-[#FFD700] transition-colors">
                    bookings@encouragewomen.com
                  </a>
                </p>
              </div>
              
              {/* WhatsApp Direct - GREEN */}
              <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <FaWhatsapp className="text-[#25D366]" /> WhatsApp Booking
                </h3>
                <a 
                  href={generalWhatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-600 hover:text-[#25D366] transition-colors text-lg flex items-center gap-2"
                >
                  <FaWhatsapp size={20} className="text-[#25D366]" />
                  +251 912 093 264
                </a>
                <p className="text-sm text-gray-500 mt-2">Instant response, typically within 1 hour</p>
              </div>
              
              {/* Follow Us - GOLD social icons on hover */}
              <div className="bg-white p-8 rounded-2xl shadow-md border border-gray-100">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Follow Us</h3>
                <div className="flex gap-4">
                  <a 
                    href="https://facebook.com/encouragewomenethiopia" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#FFD700] bg-opacity-10 hover:bg-[#FFD700] w-12 h-12 rounded-full flex items-center justify-center text-[#FFD700] hover:text-white transition-all duration-300"
                  >
                    <FaFacebook size={20} />
                  </a>
                  <a 
                    href="https://instagram.com/encouragewomenethiopia" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#FFD700] bg-opacity-10 hover:bg-[#FFD700] w-12 h-12 rounded-full flex items-center justify-center text-[#FFD700] hover:text-white transition-all duration-300"
                  >
                    <FaInstagram size={20} />
                  </a>
                  <a 
                    href="https://twitter.com/encouragewomen" 
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-[#FFD700] bg-opacity-10 hover:bg-[#FFD700] w-12 h-12 rounded-full flex items-center justify-center text-[#FFD700] hover:text-white transition-all duration-300"
                  >
                    <FaTwitter size={20} />
                  </a>
                </div>
              </div>
            </div>
            
            {/* RIGHT COLUMN - Contact Form */}
            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-lg border border-gray-100">
              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Send Us a Message</h3>
              <p className="text-gray-600 mb-8">We'll respond within 24 hours</p>
              
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">First Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        placeholder="Your first name"
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Last Name</label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <FaUser className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Your last name"
                        className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent bg-white"
                      />
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Email Address</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaEnvelope className="text-gray-400" />
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="your@email.com"
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent bg-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Interested In</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FaMapMarkerAlt className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      name="interestedIn"
                      value={formData.interestedIn}
                      onChange={handleChange}
                      placeholder="e.g., Historic Northern Route"
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent bg-white"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Your Message</label>
                  <div className="relative">
                    <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                      <FaComment className="text-gray-400" />
                    </div>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows="5"
                      placeholder="Tell us about your dream trip..."
                      className="w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#FFD700] focus:border-transparent bg-white resize-vertical"
                    />
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4">
                  {/* GOLD Send Message button */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`flex-1 bg-[#FFD700] hover:bg-[#FFC800] text-gray-900 font-bold py-4 px-6 rounded-lg transition-all duration-200 text-lg shadow-lg hover:shadow-xl ${
                      isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
                    }`}
                  >
                    {isSubmitting ? 'Sending...' : 'Send Message'}
                  </button>
                  
                  {/* GREEN WhatsApp button */}
                  <a
                    href={generalWhatsappUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 bg-[#25D366] hover:bg-[#20BA5C] text-white font-bold py-4 px-6 rounded-lg transition-all duration-200 text-lg shadow-lg hover:shadow-xl inline-flex items-center justify-center gap-2"
                  >
                    <FaWhatsapp size={20} />
                    WhatsApp
                  </a>
                </div>
                
                {submitStatus === 'success' && (
                  <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg text-center">
                    Thank you! Your message has been sent successfully. We'll get back to you soon.
                  </div>
                )}
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;